import { useState } from "react";
import { Link } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import {
  DocumentKind,
  LoadStatus,
  StopKind,
  canTransition,
  detentionPay,
  formatUsd,
  loadProgress,
  type Document,
  type Load,
  type LoadStop,
} from "@haulsheet/core";
import { Button } from "../ui/Button";
import { getDb } from "../../db/dexie";
import { useNow } from "../../hooks/useNow";
import { formatElapsed } from "../../logs/detention";
import { LifecycleError, cancelLoad, doBack, doNext, recordPaperPod, stopLabel } from "../../loads/lifecycle";
import type { MeResponse } from "../../store/authStore";

const time = (iso: string) =>
  new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

/**
 * Where the load is and the one next thing to tap (44 px), plus undo and
 * cancel. Owners/dispatchers can move any load, drivers their own; a
 * read-only plan shows the state but no actions.
 */
export function ProgressCard({
  load,
  stops,
  docs,
  me,
  onNeedPod,
}: {
  load: Load;
  stops: readonly LoadStop[];
  docs: readonly Document[];
  me: MeResponse;
  onNeedPod: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const hasPod = docs.some((d) => d.kind === DocumentKind.pod && d.deletedAt == null);
  const progress = loadProgress(load, stops, { hasPod });
  const running = useLiveQuery(
    async () =>
      (await getDb().detention.where("loadId").equals(load.id).toArray()).find((d) => d.deletedAt == null && d.departedAt == null) ?? null,
    [load.id],
  );
  const now = useNow(running != null);

  const readOnly = me.plan.readOnly;
  const notMine = me.role === "driver" && load.driverId != null && load.driverId !== me.driverId;
  const canAct = !readOnly && !notMine;
  // Whether the actor may move this load at all (same rule the server applies).
  const cancelOk = progress.canCancel && canTransition(load, LoadStatus.cancelled, { role: me.role, actorDriverId: me.driverId }).ok;

  async function run(fn: () => Promise<void>) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await fn();
      setConfirmCancel(false);
    } catch (e) {
      setError(e instanceof LifecycleError || e instanceof Error ? e.message : "That didn't work. Try again.");
    } finally {
      setBusy(false);
    }
  }

  const lastDrop = (() => {
    for (let i = progress.stops.length - 1; i >= 0; i--) if (progress.stops[i]!.kind === StopKind.drop) return i;
    return -1;
  })();
  const showStops = load.status !== LoadStatus.considering && load.status !== LoadStatus.passed;
  const live = running
    ? detentionPay({
        arrivedAt: running.arrivedAt,
        departedAt: Math.max(now, new Date(running.arrivedAt).getTime()),
        freeHours: running.freeHours,
        rateCentsPerHour: running.rateCentsPerHour,
      })
    : null;

  return (
    <div className="card stack" data-testid="progress-card">
      <div className="row" style={{ alignItems: "center", justifyContent: "space-between" }}>
        <strong data-testid="progress-phase">{progress.phase}</strong>
        {load.status === LoadStatus.paid && load.paidAt ? <span className="muted">Paid {time(load.paidAt)}</span> : null}
      </div>

      {showStops ? (
        <ol className="stack" style={{ margin: 0, paddingLeft: "1.2rem", gap: "0.25rem" }} data-testid="progress-stops">
          {progress.stops.map((s, i) => (
            <li key={s.id ?? `v${i}`}>
              <span>{stopLabel(s, i === lastDrop)}</span> <span className="muted">— {s.place.text || "—"}</span>
              <div className="muted" style={{ fontSize: "0.9em" }}>
                {s.arrivedAt ? `In ${time(s.arrivedAt)}` : "Not there yet"}
                {s.departedAt ? ` · Out ${time(s.departedAt)}` : ""}
              </div>
            </li>
          ))}
        </ol>
      ) : null}

      {running && live ? (
        <p className="muted" style={{ margin: 0 }} data-testid="load-detention-running">
          Detention clock running · <span data-testid="load-detention-elapsed">{formatElapsed(live.hours * 3_600_000)}</span>
          {live.payCents > 0 ? ` · ${formatUsd(live.payCents)} so far` : " · in free time"}
        </p>
      ) : null}

      {load.status === LoadStatus.delivered ? (
        <p className="muted" style={{ margin: 0 }} data-testid="progress-pay">
          {load.settlementId ? (
            <>
              On this week&apos;s pay.{" "}
              <Link className="tap-link" to={`/money/settlements/${load.settlementId}`}>See the pay week</Link>
            </>
          ) : (
            "Goes on this week's pay once it syncs."
          )}
        </p>
      ) : null}

      {readOnly && (progress.next || progress.back) ? (
        <p className="muted" style={{ margin: 0 }} data-testid="progress-readonly">
          Your plan is read-only, so loads can&apos;t be updated. Renew in Settings → Plan.
        </p>
      ) : null}
      {notMine ? (
        <p className="muted" style={{ margin: 0 }}>
          This load belongs to another driver.
        </p>
      ) : null}

      {canAct && progress.next ? (
        progress.next.kind === "need_pod" ? (
          <div className="stack" style={{ gap: "0.5rem" }}>
            <Button variant="primary" data-testid="next-step" disabled={busy} onClick={onNeedPod}>
              {progress.next.label}
            </Button>
            <Button data-testid="paper-pod" disabled={busy} onClick={() => void run(() => recordPaperPod(load.id, me))}>
              POD is on paper (no photo)
            </Button>
          </div>
        ) : (
          <Button
            variant="primary"
            data-testid="next-step"
            disabled={busy}
            onClick={() => {
              const step = progress.next!;
              void run(() => doNext(load.id, step, me));
            }}
          >
            {busy ? "Saving…" : progress.next.label}
          </Button>
        )
      ) : null}

      {canAct && (progress.back || cancelOk) ? (
        <div className="row">
          {progress.back ? (
            <Button
              variant="ghost"
              data-testid="back-step"
              disabled={busy}
              onClick={() => {
                const step = progress.back!;
                void run(() => doBack(load.id, step, me));
              }}
            >
              {progress.back.label}
            </Button>
          ) : null}
          {cancelOk ? (
            <Button
              variant="danger"
              data-testid="cancel-load"
              disabled={busy}
              onClick={() => {
                if (!confirmCancel) {
                  setConfirmCancel(true);
                  return;
                }
                void run(() => cancelLoad(load.id, me));
              }}
            >
              {confirmCancel ? "Tap again to cancel the load" : "Cancel load"}
            </Button>
          ) : null}
        </div>
      ) : null}

      {error ? (
        <p className="legal-flag" role="alert" data-testid="progress-error" style={{ margin: 0 }}>
          {error}
        </p>
      ) : null}
    </div>
  );
}
